import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Container,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  Nav,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap"
import { useHistory } from "react-router-dom"
import moment from "moment"
import API from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import { useToast } from "@/components/Common/Toast"
import { Routes } from "@/routes"

export type RouteCategory = "sales" | "dispatch"

export interface UserColorTheme {
  bg: string
  text: string
  border: string
  pillBg: string
  darkBadge: string
}

/**
 * Deterministic color palette for users/agents so each person has a consistent visual identity
 */
export const getUserColorTheme = (nameOrId?: string): UserColorTheme => {
  const palette: UserColorTheme[] = [
    { bg: "#e0f2fe", text: "#0369a1", border: "#0284c7", pillBg: "#bae6fd", darkBadge: "#0284c7" }, // Ocean Blue
    { bg: "#dcfce7", text: "#15803d", border: "#16a34a", pillBg: "#bbf7d0", darkBadge: "#16a34a" }, // Forest Green
    { bg: "#f3e8ff", text: "#6b21a8", border: "#9333ea", pillBg: "#e9d5ff", darkBadge: "#7e22ce" }, // Royal Purple
    { bg: "#fef3c7", text: "#92400e", border: "#d97706", pillBg: "#fde68a", darkBadge: "#b45309" }, // Amber
    { bg: "#ffe4e6", text: "#9f1239", border: "#e11d48", pillBg: "#fecdd3", darkBadge: "#be123c" }, // Crimson Rose
    { bg: "#ccfbf1", text: "#115e59", border: "#0d9488", pillBg: "#99f6e4", darkBadge: "#0f766e" }, // Deep Teal
    { bg: "#e0e7ff", text: "#3730a3", border: "#4f46e5", pillBg: "#c7d2fe", darkBadge: "#4338ca" }, // Indigo
    { bg: "#ffedd5", text: "#9a3412", border: "#ea580c", pillBg: "#fed7aa", darkBadge: "#c2410c" }, // Sunset Orange
  ]
  if (!nameOrId || !nameOrId.trim()) return palette[0]
  let hash = 0
  for (let i = 0; i < nameOrId.length; i++) {
    hash = nameOrId.charCodeAt(i) + ((hash << 5) - hash)
  }
  const index = Math.abs(hash) % palette.length
  return palette[index]
}

export const getInitials = (name?: string): string => {
  if (!name || !name.trim()) return "??"
  const parts = name.trim().split(" ")
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

/**
 * Formats full name with full first name and abbreviated last name to save horizontal space
 * e.g. "Juan Pérez" -> "Juan P."
 *      "Juan Carlos Pérez" -> "Juan Carlos P."
 *      "Pedro Pérez Rodríguez" -> "Pedro P."
 */
export const formatShortName = (fullName?: string): string => {
  if (!fullName || !fullName.trim()) return ""
  const parts = fullName.trim().split(/\s+/)
  if (parts.length <= 1) return parts[0]
  if (parts.length === 2) {
    return `${parts[0]} ${parts[1][0].toUpperCase()}.`
  }
  const compoundFirstNames = ["juan", "jose", "josé", "maria", "maría", "ana", "luis", "carlos"]
  if (compoundFirstNames.includes(parts[0].toLowerCase())) {
    return `${parts[0]} ${parts[1]} ${parts[2][0].toUpperCase()}.`
  }
  return `${parts[0]} ${parts[1][0].toUpperCase()}.`
}

export interface RouteStop {
  id: string
  category: RouteCategory
  placeId?: string
  placeName: string
  address: string
  sector?: string
  contactPerson?: string
  stageName?: string
  date: string
  dayOfWeek: number
  orderIndex: number
  assignedTo: string
  assignedName: string
  assignedRole: "agent" | "driver"
  vehicleName?: string
  sourceType: "erp" | "manual"
  conduceNumber?: string
  invoiceNumber?: string
  tripNumber?: string
  packageCount?: number
  notes?: string
  documentUrl?: string
  status: "pending" | "in_transit" | "completed"
  completedAt?: string
}

export interface PendingBacklogItem {
  id: string
  category: RouteCategory
  placeId?: string
  placeName: string
  address: string
  sector?: string
  originalDate: string
  daysOverdue: number
  sourceType: "erp" | "manual"
  conduceNumber?: string
  invoiceNumber?: string
  packageCount?: number
  assignedName?: string
  reason?: string
}

export interface AgentOption {
  id: string
  name: string
  role: "Vendedor" | "Chofer"
}

const DAYS = [
  { dayIndex: 1, name: "Lunes", shortName: "Lun" },
  { dayIndex: 2, name: "Martes", shortName: "Mar" },
  { dayIndex: 3, name: "Miércoles", shortName: "Mié" },
  { dayIndex: 4, name: "Jueves", shortName: "Jue" },
  { dayIndex: 5, name: "Viernes", shortName: "Vie" },
  { dayIndex: 6, name: "Sábado", shortName: "Sáb" },
]

export default function WeeklyRoutesPage(): JSX.Element {
  const user: any = parsedUser()
  const { success, error: toastError, info } = useToast()
  const history = useHistory()

  // 1. Dual Mode: Sales Routes vs Dispatch (TMS)
  const [activeTab, setActiveTab] = useState<RouteCategory>("sales")

  // Week navigation state (Monday is start of ISO week)
  const [currentWeekStart, setCurrentWeekStart] = useState<moment.Moment>(
    moment().startOf("isoWeek")
  )

  // Filters & Drawer tab (empty selection = everyone)
  const [selectedAgents, setSelectedAgents] = useState<string[]>([])
  const [sourceFilter, setSourceFilter] = useState<"all" | "erp" | "manual">("all")
  const [searchBacklog, setSearchBacklog] = useState<string>("")
  const [isBacklogCollapsed, setIsBacklogCollapsed] = useState<boolean>(false)
  const [leftDrawerTab, setLeftDrawerTab] = useState<"backlog" | "cartera">("cartera")

  // Data states
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSyncing, setIsSyncing] = useState<boolean>(false)
  const [lastSyncAt, setLastSyncAt] = useState<moment.Moment | null>(null)
  const [agents, setAgents] = useState<AgentOption[]>([])
  const [weekStops, setWeekStops] = useState<RouteStop[]>([])
  const [backlogItems, setBacklogItems] = useState<PendingBacklogItem[]>([])
  const [placesList, setPlacesList] = useState<any[]>([])

  // Modals
  const [showImportModal, setShowImportModal] = useState<boolean>(false)
  const [showMapModal, setShowMapModal] = useState<boolean>(false)
  const [selectedDayForMap, setSelectedDayForMap] = useState<number>(1)
  const [showAddStopModal, setShowAddStopModal] = useState<boolean>(false)
  const [selectedDayForAdd, setSelectedDayForAdd] = useState<number>(1)
  const [selectedStopDetail, setSelectedStopDetail] = useState<RouteStop | null>(null)

  // Import modal state (PDF / Image)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [isExtracting, setIsExtracting] = useState<boolean>(false)
  const [extractedData, setExtractedData] = useState<{
    conduceNumber: string
    invoiceNumber: string
    clientName: string
    address: string
    packageCount: number
    targetDay: number
  } | null>(null)

  // New Stop form (supports Place search)
  const [newStopForm, setNewStopForm] = useState({
    placeId: "",
    placeName: "",
    address: "",
    sector: "",
    contactPerson: "",
    stageName: "Terminación",
    conduceNumber: "",
    invoiceNumber: "",
    sourceType: "manual" as "erp" | "manual",
    dayOfWeek: 1,
    assignedTo: "",
    assignedName: "",
    packageCount: 1,
    notes: "",
  })

  // Date range display
  const weekStartStr = currentWeekStart.format("DD MMM")
  const weekEndStr = moment(currentWeekStart).add(5, "days").format("DD MMM YYYY")
  const weekNumber = currentWeekStart.isoWeek()
  const weekKey = currentWeekStart.format("GGGG-[W]WW")
  const isCurrentWeek = currentWeekStart.isSame(moment().startOf("isoWeek"), "day")

  const orgId: string | undefined = user?.OrganizationId
  // Prefix of the browser-only storage used before routes were saved on the server
  const legacyStoragePrefix = `shearly_weekly_routes_${orgId}_`

  // Weekday dates map
  const weekDates = useMemo(() => {
    return DAYS.map((d) => {
      const dateMoment = moment(currentWeekStart).add(d.dayIndex - 1, "days")
      return {
        ...d,
        dateStr: dateMoment.format("YYYY-MM-DD"),
        displayDate: dateMoment.format("DD MMM"),
        isToday: dateMoment.isSame(moment(), "day"),
      }
    })
  }, [currentWeekStart])

  const mapServerStop = (x: any): RouteStop => ({
    id: x.id,
    category: x.category,
    placeId: x.placeId || undefined,
    placeName: x.placeName,
    address: x.address || "",
    sector: x.sector || undefined,
    contactPerson: x.contactPerson || undefined,
    stageName: x.stageName || undefined,
    date: x.date,
    dayOfWeek: x.dayOfWeek,
    orderIndex: x.orderIndex,
    assignedTo: x.assignedTo || "",
    assignedName: x.assignedName || "Sin asignar",
    assignedRole: x.assignedRole === "driver" ? "driver" : "agent",
    vehicleName: x.vehicleName || undefined,
    sourceType: x.sourceType === "erp" ? "erp" : "manual",
    conduceNumber: x.conduceNumber || undefined,
    invoiceNumber: x.invoiceNumber || undefined,
    tripNumber: x.tripNumber || undefined,
    packageCount: x.packageCount ?? undefined,
    notes: x.notes || undefined,
    documentUrl: x.documentUrl || undefined,
    status: x.status,
    completedAt: x.completedAt ? moment(x.completedAt).format("ddd DD, hh:mm A") : undefined,
  })

  const toPayload = (stop: RouteStop) => ({ ...stop, completedAt: undefined })

  // Fetch the week from the server (both categories)
  const loadStops = useCallback(
    async (silent = false) => {
      if (!orgId) return
      try {
        const res = await API.getAction("WeeklyRoutes/List", `OrganizationId=${orgId}&WeekKey=${weekKey}`)
        if (res.data?.status === "ok" && Array.isArray(res.data.response)) {
          setWeekStops(res.data.response.map(mapServerStop))
          setLastSyncAt(moment())
        }
      } catch (err) {
        if (!silent) toastError("Rutas", "No se pudieron cargar las rutas desde el servidor.")
      }
    },
    [orgId, weekKey]
  )

  // Upsert stops on the server; on failure reload the truth from the server
  const persistStops = async (stops: RouteStop[]) => {
    if (!orgId || stops.length === 0) return
    try {
      const res = await API.postAction("WeeklyRoutes/SaveBatch", {
        OrganizationId: orgId,
        Stops: stops.map(toPayload),
      })
      if (res.data?.status !== "ok") throw new Error(res.data?.errorCode)
      setLastSyncAt(moment())
    } catch (err) {
      toastError("Rutas", "No se pudo guardar el cambio en el servidor.")
      loadStops(true)
    }
  }

  const addStops = (stops: RouteStop[]) => {
    setWeekStops((prev) => [...prev, ...stops])
    persistStops(stops)
  }

  // Routes created before server persistence live only in this browser.
  const collectLegacyStops = () => {
    const keys: string[] = []
    const stops: RouteStop[] = []
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (!key || !key.startsWith(legacyStoragePrefix)) continue
        keys.push(key)
        const parsed = JSON.parse(localStorage.getItem(key) || "[]")
        if (Array.isArray(parsed)) stops.push(...parsed)
      }
    } catch (err) {
      console.warn("Could not read local weekly routes", err)
    }
    return { keys, stops }
  }

  // Sync: upload browser-only routes (if any) and refresh from the server
  const handleSync = async (silent = false) => {
    if (!orgId) return
    setIsSyncing(true)
    try {
      const { keys, stops } = collectLegacyStops()
      if (stops.length > 0) {
        const res = await API.postAction("WeeklyRoutes/SaveBatch", {
          OrganizationId: orgId,
          Stops: stops.map(toPayload),
        })
        if (res.data?.status !== "ok") throw new Error(res.data?.errorCode)
        // Keep a backup copy under a different prefix, then stop re-uploading it
        keys.forEach((key) => {
          localStorage.setItem(
            key.replace("shearly_weekly_routes_", "shearly_weekly_routes_backup_"),
            localStorage.getItem(key) || "[]"
          )
          localStorage.removeItem(key)
        })
        const r = res.data.response || {}
        success(
          "Sincronización",
          `${r.created || 0} rutas de este navegador subidas al servidor` +
            (r.skipped ? ` (${r.skipped} omitidas)` : "") +
            "."
        )
      }
      await loadStops(silent)
      if (!silent && stops.length === 0) success("Sincronización", "Rutas actualizadas desde el servidor.")
    } catch (err) {
      toastError("Sincronización", "No se pudo sincronizar con el servidor. Intente de nuevo.")
    } finally {
      setIsSyncing(false)
    }
  }

  // Initial load
  const loadInitialData = useCallback(async () => {
    if (!user?.OrganizationId) return
    setIsLoading(true)

    try {
      // 1. Load Agents / Staff from API
      try {
        const agentsRes = await API.getAction(
          "agents/Summary",
          `OrganizationId=${user.OrganizationId}&Page=0&Quantity=200`
        )
        if (agentsRes.data?.status === "ok" && Array.isArray(agentsRes.data.response)) {
          const mappedAgents: AgentOption[] = agentsRes.data.response.map((a: any) => ({
            id: a.id || a.agentId || a.userId,
            name: `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.name || "Agente",
            role: /driver|chofer|transport/i.test(a.roleName || a.role || "") ? "Chofer" : "Vendedor",
          }))
          setAgents(mappedAgents)
        }
      } catch (err) {
        console.warn("Could not load real agents, using default roster", err)
      }

      // 2. Load real places from API (All Places in DB)
      let realPlaces: any[] = []
      try {
        const placesRes = await API.getAction(
          "place/list",
          `OrganizationId=${user.OrganizationId}&Quantity=100&includeVisitStats=false`
        )
        if (placesRes.data?.status === "ok" && Array.isArray(placesRes.data.response)) {
          realPlaces = placesRes.data.response
          setPlacesList(realPlaces)
        }
      } catch (err) {
        console.warn("Could not load places list", err)
      }

      // 3. Load real pending revisits from API (revisitPending=true)
      let realRevisits: any[] = []
      try {
        const revisitsRes = await API.getAction(
          "place/list",
          `OrganizationId=${user.OrganizationId}&revisitPending=true&includeVisitStats=false`
        )
        if (revisitsRes.data?.status === "ok" && Array.isArray(revisitsRes.data.response)) {
          realRevisits = revisitsRes.data.response
        }
      } catch (err) {
        console.warn("Could not load real pending revisits", err)
      }

      // 5. Build Backlog Items (Pending revisits & overdue places)
      let salesBacklog: PendingBacklogItem[] = []
      if (realRevisits.length > 0) {
        salesBacklog = realRevisits.map((p, idx) => ({
          id: `bkl-rev-${p.id}`,
          category: "sales",
          placeId: p.id,
          placeName: p.name,
          address: p.address || "Dirección registrada",
          sector: p.sector || p.city || "",
          originalDate: moment(currentWeekStart).subtract(4 + idx, "days").format("YYYY-MM-DD"),
          daysOverdue: 4 + idx,
          sourceType: "manual",
          assignedName: p.assignedToObj
            ? `${p.assignedToObj.firstName || ""} ${p.assignedToObj.lastName || ""}`.trim()
            : "Vendedor Asignado",
          reason: "Re-visita técnica pendiente de semanas anteriores",
        }))
      }

      const dispatchBacklog: PendingBacklogItem[] = []
      setBacklogItems([...salesBacklog, ...dispatchBacklog])
    } finally {
      setIsLoading(false)
    }
  }, [currentWeekStart, user?.OrganizationId])

  useEffect(() => {
    loadInitialData()
  }, [loadInitialData])

  // Week stops: upload any browser-only routes, then load from the server
  useEffect(() => {
    handleSync(true)
  }, [orgId, weekKey])

  // Keep the board fresh so status changes from sellers/drivers show up
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") loadStops(true)
    }, 60000)
    return () => window.clearInterval(timer)
  }, [loadStops])

  // Navigate weeks
  const handlePrevWeek = () => {
    setCurrentWeekStart((prev) => moment(prev).subtract(1, "week").startOf("isoWeek"))
  }

  const handleNextWeek = () => {
    setCurrentWeekStart((prev) => moment(prev).add(1, "week").startOf("isoWeek"))
  }

  const handleCurrentWeek = () => {
    setCurrentWeekStart(moment().startOf("isoWeek"))
  }

  // Active Category stops & backlog
  const activeStops = useMemo(() => {
    return weekStops.filter((stop) => {
      if (stop.category !== activeTab) return false
      if (selectedAgents.length > 0 && !selectedAgents.includes(stop.assignedTo)) return false
      if (sourceFilter !== "all" && stop.sourceType !== sourceFilter) return false
      return true
    })
  }, [weekStops, activeTab, selectedAgents, sourceFilter])

  const activeBacklog = useMemo(() => {
    return backlogItems.filter((item) => {
      if (item.category !== activeTab) return false
      if (sourceFilter !== "all" && item.sourceType !== sourceFilter) return false
      if (searchBacklog.trim()) {
        const query = searchBacklog.toLowerCase()
        const matchesClient = item.placeName.toLowerCase().includes(query)
        const matchesSector = item.sector && item.sector.toLowerCase().includes(query)
        if (!matchesClient && !matchesSector) return false
      }
      return true
    })
  }, [backlogItems, activeTab, sourceFilter, searchBacklog])

  // Filtered Cartera list for instant planning
  const filteredCartera = useMemo(() => {
    const scheduledPlaceIds = new Set(
      weekStops.filter((s) => s.category === activeTab).map((s) => s.placeId)
    )

    return placesList.filter((place) => {
      // Don't show places already scheduled in this week's active tab
      if (scheduledPlaceIds.has(place.id)) return false

      if (searchBacklog.trim()) {
        const q = searchBacklog.toLowerCase()
        const matchesName = (place.name || "").toLowerCase().includes(q)
        const matchesSector = (place.sector || place.city || "").toLowerCase().includes(q)
        const matchesAddr = (place.address || "").toLowerCase().includes(q)
        if (!matchesName && !matchesSector && !matchesAddr) return false
      }
      return true
    })
  }, [placesList, weekStops, activeTab, searchBacklog])

  // Metrics for active tab
  const metrics = useMemo(() => {
    const total = activeStops.length
    const completed = activeStops.filter((s) => s.status === "completed").length
    const inTransit = activeStops.filter((s) => s.status === "in_transit").length
    const pending = activeStops.filter((s) => s.status === "pending").length
    const completionPercent = total > 0 ? Math.round((completed / total) * 100) : 0
    const totalPackages = activeStops.reduce((acc, s) => acc + (s.packageCount || 0), 0)

    return {
      total,
      completed,
      inTransit,
      pending,
      completionPercent,
      totalPackages,
      backlogCount: activeBacklog.length,
      carteraCount: placesList.length,
    }
  }, [activeStops, activeBacklog, placesList])

  const tabAgents = useMemo(() => {
    const role = activeTab === "sales" ? "Vendedor" : "Chofer"
    const matching = agents.filter((a) => a.role === role)
    return matching.length > 0 ? matching : agents
  }, [agents, activeTab])

  // Reorder stop sequence within a day
  const handleReorder = (stopId: string, direction: "up" | "down") => {
    const stop = weekStops.find((s) => s.id === stopId)
    if (!stop) return

    const dayStops = weekStops
      .filter((s) => s.category === activeTab && s.dayOfWeek === stop.dayOfWeek)
      .sort((a, b) => a.orderIndex - b.orderIndex)

    const currentIndex = dayStops.findIndex((s) => s.id === stopId)
    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1
    if (targetIndex < 0 || targetIndex >= dayStops.length) return

    const reordered = [...dayStops]
    ;[reordered[currentIndex], reordered[targetIndex]] = [reordered[targetIndex], reordered[currentIndex]]
    const changed = reordered
      .map((s, idx) => ({ ...s, orderIndex: idx + 1 }))
      .filter((s, idx) => dayStops.find((d) => d.id === s.id)?.orderIndex !== idx + 1)

    setWeekStops((prev) => prev.map((s) => changed.find((c) => c.id === s.id) || s))
    persistStops(changed)
  }

  // Change stop to another day
  const handleChangeDay = (stopId: string, newDay: number) => {
    const targetDateStr = weekDates.find((w) => w.dayIndex === newDay)?.dateStr
    if (!targetDateStr) return

    const stop = weekStops.find((s) => s.id === stopId)
    if (!stop) return
    const targetDayCount = weekStops.filter(
      (s) => s.category === activeTab && s.dayOfWeek === newDay
    ).length
    const moved: RouteStop = { ...stop, dayOfWeek: newDay, date: targetDateStr, orderIndex: targetDayCount + 1 }

    setWeekStops((prev) => prev.map((s) => (s.id === stopId ? moved : s)))
    persistStops([moved])

    const dayName = DAYS.find((d) => d.dayIndex === newDay)?.name || "el día"
    success("Rutas", `Visita movida a ${dayName}.`)
  }

  // Remove stop from day and optionally return to backlog
  const handleRemoveStop = (stop: RouteStop) => {
    setWeekStops((prev) => prev.filter((s) => s.id !== stop.id))
    API.getAction("WeeklyRoutes/Delete", `Id=${encodeURIComponent(stop.id)}`)
      .then((res) => {
        if (res.data?.status !== "ok") throw new Error()
        setLastSyncAt(moment())
      })
      .catch(() => {
        toastError("Rutas", "No se pudo eliminar en el servidor.")
        loadStops(true)
      })

    if (stop.placeId) {
      const newBacklog: PendingBacklogItem = {
        id: `bkl-ret-${Date.now()}`,
        category: stop.category,
        placeId: stop.placeId,
        placeName: stop.placeName,
        address: stop.address,
        sector: stop.sector,
        originalDate: stop.date,
        daysOverdue: 1,
        sourceType: stop.sourceType,
        conduceNumber: stop.conduceNumber,
        invoiceNumber: stop.invoiceNumber,
        assignedName: stop.assignedName,
        reason: "Devuelta a la bandeja de pendientes",
      }
      setBacklogItems((prev) => [newBacklog, ...prev])
    }

    info("Rutas", `"${stop.placeName}" desasignada de la ruta semanal.`)
  }

  // Schedule a place directly from Cartera to a specific day
  const handleScheduleFromCartera = (place: any, targetDay: number) => {
    const targetDateObj = weekDates.find((w) => w.dayIndex === targetDay)
    const existingInDay = weekStops.filter(
      (s) => s.category === activeTab && s.dayOfWeek === targetDay
    )

    const assignedName = place.assignedToObj
      ? `${place.assignedToObj.firstName || ""} ${place.assignedToObj.lastName || ""}`.trim()
      : agents[0]?.name || "Vendedor Asignado"

    const newStop: RouteStop = {
      id: `stp-${Date.now()}-${place.id}`,
      category: activeTab,
      placeId: place.id,
      placeName: place.name,
      address: place.address || "Dirección registrada",
      sector: place.sector || place.city || "",
      contactPerson: place.contactName || "Encargado de obra",
      stageName: place.stage?.name || "En seguimiento",
      date: targetDateObj?.dateStr || "",
      dayOfWeek: targetDay,
      orderIndex: existingInDay.length + 1,
      assignedTo: place.assignedTo || agents[0]?.id || "",
      assignedName: assignedName,
      assignedRole: activeTab === "sales" ? "agent" : "driver",
      sourceType: "manual",
      status: "pending",
      notes: "Visita comercial programada",
    }

    addStops([newStop])

    const dayName = targetDateObj?.name || "Día seleccionado"
    success("Rutas", `"${place.name}" programado para el ${dayName}.`)
  }

  // Assign backlog item to a day
  const handleAssignBacklogItem = (item: PendingBacklogItem, targetDay: number) => {
    const targetDateObj = weekDates.find((w) => w.dayIndex === targetDay)
    const existingInDay = weekStops.filter(
      (s) => s.category === activeTab && s.dayOfWeek === targetDay
    )

    const newStop: RouteStop = {
      id: `stp-bkl-${Date.now()}`,
      category: item.category,
      placeId: item.placeId,
      placeName: item.placeName,
      address: item.address,
      sector: item.sector,
      date: targetDateObj?.dateStr || "",
      dayOfWeek: targetDay,
      orderIndex: existingInDay.length + 1,
      assignedTo: agents[0]?.id || "",
      assignedName: item.assignedName || agents[0]?.name || "Vendedor Asignado",
      assignedRole: item.category === "sales" ? "agent" : "driver",
      sourceType: item.sourceType,
      conduceNumber: item.conduceNumber,
      invoiceNumber: item.invoiceNumber,
      packageCount: item.packageCount || 1,
      status: "pending",
    }

    addStops([newStop])

    setBacklogItems((prev) => prev.filter((b) => b.id !== item.id))
    const dayName = targetDateObj?.name || "el día"
    success("Rutas", `"${item.placeName}" asignado al ${dayName}.`)
  }

  // Clone week to next week (server side)
  const handleCloneToNextWeek = async () => {
    const nextWeekMoment = moment(currentWeekStart).add(1, "week").startOf("isoWeek")
    const nextWeekKey = nextWeekMoment.format("GGGG-[W]WW")

    if (weekStops.filter((s) => s.category === activeTab).length === 0) {
      toastError("Rutas", "No hay paradas en esta semana para copiar.")
      return
    }

    try {
      const res = await API.postAction("WeeklyRoutes/CloneWeek", {
        OrganizationId: orgId,
        Category: activeTab,
        FromWeekKey: weekKey,
        ToWeekKey: nextWeekKey,
      })
      if (res.data?.status === "ok") {
        success(
          "Rutas Semanales",
          `${res.data.response.length} paradas copiadas a la semana del ${nextWeekMoment.format("DD MMM")}.`
        )
      } else if (res.data?.errorCode === "target_week_not_empty") {
        toastError("Rutas", "La semana siguiente ya tiene rutas. No se copió nada.")
      } else {
        throw new Error(res.data?.errorCode)
      }
    } catch (err: any) {
      const code = err?.response?.data?.errorCode
      toastError(
        "Rutas",
        code === "target_week_not_empty"
          ? "La semana siguiente ya tiene rutas. No se copió nada."
          : "No se pudo copiar la semana."
      )
    }
  }

  // Toggle status (pending -> in_transit -> completed)
  const handleToggleStatus = async (stopId: string) => {
    const stop = weekStops.find((s) => s.id === stopId)
    if (!stop) return
    const nextStatus: RouteStop["status"] =
      stop.status === "completed" ? "pending" : stop.status === "pending" ? "in_transit" : "completed"

    setWeekStops((prev) =>
      prev.map((s) =>
        s.id === stopId
          ? { ...s, status: nextStatus, completedAt: nextStatus === "completed" ? moment().format("ddd DD, hh:mm A") : undefined }
          : s
      )
    )

    try {
      const res = await API.postAction("WeeklyRoutes/UpdateStatus", { Id: stopId, Status: nextStatus })
      if (res.data?.status !== "ok") throw new Error()
      const saved = mapServerStop(res.data.response)
      setWeekStops((prev) => prev.map((s) => (s.id === stopId ? saved : s)))
      setLastSyncAt(moment())
    } catch (err) {
      toastError("Rutas", "No se pudo actualizar el estado en el servidor.")
      loadStops(true)
    }
  }

  // File Upload & OCR / Parser Simulation for Despachos
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImportFile(file)
    setIsExtracting(true)

    setTimeout(() => {
      const isPdf = file.name.endsWith(".pdf")
      const randomId = Math.floor(1000 + Math.random() * 9000)
      setExtractedData({
        conduceNumber: `CD-9${randomId}`,
        invoiceNumber: `F-10${randomId}`,
        clientName: isPdf ? "Ferretería Ochoa Santiago" : "Constructora Del Yaque S.R.L.",
        address: "Av. 27 de Febrero esq. Imbert",
        packageCount: 12,
        targetDay: 1,
      })
      setIsExtracting(false)
      success("Despachos", `Archivo ${file.name} procesado correctamente. Datos extraídos.`)
    }, 1000)
  }

  const handleConfirmImport = (target: "week" | "backlog") => {
    if (!extractedData) return

    if (target === "week") {
      const targetDateObj = weekDates.find((w) => w.dayIndex === extractedData.targetDay)
      const existingInDay = weekStops.filter(
        (s) => s.category === "dispatch" && s.dayOfWeek === extractedData.targetDay
      )

      const defaultDriver = agents.find((a) => a.role === "Chofer") || agents[0]

      const newStop: RouteStop = {
        id: `stp-imp-${Date.now()}`,
        category: "dispatch",
        placeId: `plc-imp-${Date.now()}`,
        placeName: extractedData.clientName,
        address: extractedData.address,
        date: targetDateObj?.dateStr || "",
        dayOfWeek: extractedData.targetDay,
        orderIndex: existingInDay.length + 1,
        assignedTo: selectedAgents.length === 1 ? selectedAgents[0] : defaultDriver?.id || "",
        assignedName:
          (selectedAgents.length === 1
            ? agents.find((a) => a.id === selectedAgents[0])?.name
            : defaultDriver?.name) || "Sin asignar",
        assignedRole: "driver",
        sourceType: "manual",
        conduceNumber: extractedData.conduceNumber,
        invoiceNumber: extractedData.invoiceNumber,
        documentUrl: importFile?.name,
        packageCount: extractedData.packageCount,
        status: "pending",
      }

      addStops([newStop])
      success("Rutas", `Despacho importado y asignado a ${targetDateObj?.name || "la semana"}`)
    } else {
      const newBacklog: PendingBacklogItem = {
        id: `bkl-imp-${Date.now()}`,
        category: "dispatch",
        placeId: `plc-imp-${Date.now()}`,
        placeName: extractedData.clientName,
        address: extractedData.address,
        originalDate: moment().format("YYYY-MM-DD"),
        daysOverdue: 0,
        sourceType: "manual",
        conduceNumber: extractedData.conduceNumber,
        invoiceNumber: extractedData.invoiceNumber,
        packageCount: extractedData.packageCount,
        reason: "Importado manualmente desde archivo",
      }
      setBacklogItems((prev) => [newBacklog, ...prev])
      info("Rutas", `Despacho importado a la bandeja de pendientes sin asignar.`)
    }

    setShowImportModal(false)
    setImportFile(null)
    setExtractedData(null)
  }

  // Create manual stop / visit
  const handleCreateStop = () => {
    if (!newStopForm.placeName.trim()) {
      toastError("Rutas", "Ingrese el nombre del cliente o seleccione un lugar.")
      return
    }

    const targetDateObj = weekDates.find((w) => w.dayIndex === newStopForm.dayOfWeek)
    const existingInDay = weekStops.filter(
      (s) => s.category === activeTab && s.dayOfWeek === newStopForm.dayOfWeek
    )

    const assignedAgentObj = agents.find((a) => a.id === newStopForm.assignedTo)

    const newStop: RouteStop = {
      id: `stp-new-${Date.now()}`,
      category: activeTab,
      placeId: newStopForm.placeId || `plc-new-${Date.now()}`,
      placeName: newStopForm.placeName,
      address: newStopForm.address || "Dirección no especificada",
      sector: newStopForm.sector,
      contactPerson: newStopForm.contactPerson,
      stageName: newStopForm.stageName,
      date: targetDateObj?.dateStr || "",
      dayOfWeek: newStopForm.dayOfWeek,
      orderIndex: existingInDay.length + 1,
      assignedTo:
        assignedAgentObj?.id || (selectedAgents.length === 1 ? selectedAgents[0] : agents[0]?.id) || "",
      assignedName:
        assignedAgentObj?.name ||
        (selectedAgents.length === 1
          ? agents.find((a) => a.id === selectedAgents[0])?.name
          : agents[0]?.name) ||
        "Sin asignar",
      assignedRole: activeTab === "sales" ? "agent" : "driver",
      sourceType: newStopForm.sourceType,
      conduceNumber: newStopForm.conduceNumber || undefined,
      invoiceNumber: newStopForm.invoiceNumber || undefined,
      packageCount: Number(newStopForm.packageCount) || 1,
      notes: newStopForm.notes,
      status: "pending",
    }

    addStops([newStop])

    setShowAddStopModal(false)
    setNewStopForm({
      placeId: "",
      placeName: "",
      address: "",
      sector: "",
      contactPerson: "",
      stageName: "Terminación",
      conduceNumber: "",
      invoiceNumber: "",
      sourceType: "manual",
      dayOfWeek: 1,
      assignedTo: "",
      assignedName: "",
      packageCount: 1,
      notes: "",
    })
    success("Rutas", `Parada agregada al ${targetDateObj?.name}`)
  }

  // Handle selecting an existing place in the creation modal
  const handleSelectExistingPlace = (placeId: string) => {
    const found = placesList.find((p) => p.id === placeId)
    if (!found) return
    setNewStopForm((prev) => ({
      ...prev,
      placeId: found.id,
      placeName: found.name,
      address: found.address || "",
      sector: found.sector || found.city || "",
      stageName: found.stage?.name || "Activo",
      assignedTo: found.assignedTo || prev.assignedTo,
      assignedName: found.assignedToObj
        ? `${found.assignedToObj.firstName || ""} ${found.assignedToObj.lastName || ""}`.trim()
        : prev.assignedName,
    }))
  }

  return (
    <Container fluid className="px-3 py-3 pb-5">
      {/* 1. Single top bar: category toggle, week, people filter, sync & actions */}
      <div className="bg-white p-2 px-3 rounded-4 shadow-sm border mb-3 d-flex flex-wrap align-items-center gap-2">
        <ButtonGroup className="rounded-pill border p-1 bg-light">
          {(
            [
              { key: "sales", label: "Vendedores", icon: "fa-user-tie", color: "primary" },
              { key: "dispatch", label: "Transporte", icon: "fa-truck", color: "success" },
            ] as const
          ).map((tab) => {
            const active = activeTab === tab.key
            return (
              <Button
                key={tab.key}
                size="sm"
                variant={active ? tab.color : "light"}
                onClick={() => {
                  setActiveTab(tab.key)
                  setSelectedAgents([])
                }}
                className={`rounded-pill fw-bold px-3 d-flex align-items-center gap-2 ${active ? "shadow-sm" : "text-secondary border-0"}`}
              >
                <i className={`fas ${tab.icon}`} />
                {tab.label}
                <Badge bg={active ? "light" : "secondary"} className={active ? `text-${tab.color}` : "text-white"}>
                  {weekStops.filter((s) => s.category === tab.key).length}
                </Badge>
              </Button>
            )
          })}
        </ButtonGroup>

        {/* Week Navigator */}
        <ButtonGroup size="sm" className="rounded-pill border">
          <Button variant="light" onClick={handlePrevWeek} title="Semana anterior" className="px-2 text-secondary">
            <i className="fas fa-chevron-left" />
          </Button>
          <Button
            variant={isCurrentWeek ? "primary" : "light"}
            onClick={handleCurrentWeek}
            title="Ir a la semana actual"
            className="fw-bold px-3 d-flex align-items-center gap-1"
          >
            <i className="far fa-calendar-alt" />
            Sem. {weekNumber} · {weekStartStr} - {weekEndStr}
          </Button>
          <Button variant="light" onClick={handleNextWeek} title="Semana siguiente" className="px-2 text-secondary">
            <i className="fas fa-chevron-right" />
          </Button>
        </ButtonGroup>

        {/* People filter (multi-select) */}
        <Dropdown autoClose="outside">
          <Dropdown.Toggle
            size="sm"
            variant={selectedAgents.length > 0 ? "outline-primary" : "outline-secondary"}
            className="rounded-pill fw-semibold d-flex align-items-center gap-2"
          >
            <i className={`fas ${activeTab === "sales" ? "fa-users" : "fa-truck"}`} />
            {selectedAgents.length === 0
              ? activeTab === "sales"
                ? "Todos los vendedores"
                : "Todos los choferes"
              : selectedAgents.length === 1
              ? agents.find((a) => a.id === selectedAgents[0])?.name || "1 seleccionado"
              : `${selectedAgents.length} seleccionados`}
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ maxHeight: 360, overflowY: "auto", minWidth: 260 }} className="shadow">
            <div className="px-3 py-1">
              <Form.Check
                type="checkbox"
                id="agent-filter-all"
                label={<span className="fw-bold">Todos</span>}
                checked={selectedAgents.length === 0}
                onChange={() => setSelectedAgents([])}
              />
            </div>
            <Dropdown.Divider />
            {tabAgents.length === 0 && <div className="px-3 py-1 text-muted small">Sin personal registrado</div>}
            {tabAgents.map((agt) => (
              <div key={agt.id} className="px-3 py-1 d-flex align-items-center justify-content-between gap-2">
                <Form.Check
                  type="checkbox"
                  id={`agent-filter-${agt.id}`}
                  label={agt.name}
                  checked={selectedAgents.includes(agt.id)}
                  onChange={() =>
                    setSelectedAgents((prev) =>
                      prev.includes(agt.id) ? prev.filter((id) => id !== agt.id) : [...prev, agt.id]
                    )
                  }
                />
                <Badge bg="light" text="secondary" pill>
                  {weekStops.filter((s) => s.category === activeTab && s.assignedTo === agt.id).length}
                </Badge>
              </div>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        {/* Sync */}
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handleSync(false)}
            disabled={isSyncing}
            title="Subir rutas guardadas en este navegador y actualizar desde el servidor"
            className="rounded-pill d-flex align-items-center gap-1"
          >
            {isSyncing ? <Spinner animation="border" size="sm" /> : <i className="fas fa-sync-alt" />}
            Sincronizar
          </Button>
          {lastSyncAt && (
            <small className="text-muted text-nowrap" title={lastSyncAt.format("DD MMM YYYY hh:mm:ss A")}>
              <i className="fas fa-cloud text-success me-1" />
              {lastSyncAt.format("hh:mm A")}
            </small>
          )}
        </div>

        {/* Action CTAs */}
        <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleCloneToNextWeek}
            title="Copiar las rutas de esta semana a la semana siguiente"
            className="rounded-pill d-flex align-items-center gap-1"
          >
            <i className="fas fa-clone" />
            Copiar semana
          </Button>

          {activeTab === "dispatch" && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowImportModal(true)}
              className="rounded-pill fw-semibold d-flex align-items-center gap-1"
            >
              <i className="fas fa-file-upload" />
              Importar conduce
            </Button>
          )}

          <Button
            variant={activeTab === "sales" ? "primary" : "success"}
            size="sm"
            onClick={() => {
              setSelectedDayForAdd(1)
              setNewStopForm((prev) => ({ ...prev, dayOfWeek: 1 }))
              setShowAddStopModal(true)
            }}
            className="rounded-pill fw-semibold d-flex align-items-center gap-1 shadow-sm"
          >
            <i className="fas fa-plus" />
            {activeTab === "sales" ? "Agendar visita" : "Nueva entrega"}
          </Button>
        </div>
      </div>

      {/* 2. Performance Metrics Banner for Active Tab */}
      <Row className="g-3 mb-3">
        {/* KPI 1: Programadas */}
        <Col xs={12} sm={6} md={4} lg={2.4} className="col-lg">
          <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
            <Card.Body className="p-3 d-flex align-items-center gap-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-primary bg-primary-subtle"
                style={{ width: "46px", height: "46px" }}
              >
                <i className="far fa-calendar-check fs-5" />
              </div>
              <div>
                <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                  {activeTab === "sales" ? "Visitas Programadas" : "Total Paradas"}
                </div>
                <div className="fs-4 fw-bold text-dark">{metrics.total}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* KPI 2: Realizadas */}
        <Col xs={12} sm={6} md={4} lg={2.4} className="col-lg">
          <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
            <Card.Body className="p-3 d-flex align-items-center gap-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-success bg-success-subtle"
                style={{ width: "46px", height: "46px" }}
              >
                <i className="fas fa-check-double fs-5" />
              </div>
              <div>
                <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                  {activeTab === "sales" ? "Visitas Realizadas" : "Entregas Listas"}
                </div>
                <div className="fs-4 fw-bold text-success d-flex align-items-baseline gap-1">
                  {metrics.completed}
                  <span className="small text-muted fw-normal" style={{ fontSize: "12px" }}>
                    ({metrics.completionPercent}%)
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* KPI 3: Pendientes */}
        <Col xs={12} sm={6} md={4} lg={2.4} className="col-lg">
          <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
            <Card.Body className="p-3 d-flex align-items-center gap-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-warning bg-warning-subtle"
                style={{ width: "46px", height: "46px" }}
              >
                <i className="far fa-clock fs-5" />
              </div>
              <div>
                <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                  {activeTab === "sales" ? "Visitas Pendientes" : "Entregas Pendientes"}
                </div>
                <div className="fs-4 fw-bold text-secondary">{metrics.pending}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* KPI 4: Cartera DB */}
        <Col xs={12} sm={6} md={4} lg={2.4} className="col-lg">
          <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
            <Card.Body className="p-3 d-flex align-items-center gap-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-info bg-info-subtle"
                style={{ width: "46px", height: "46px" }}
              >
                <i className="fas fa-building fs-5" />
              </div>
              <div>
                <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                  {activeTab === "sales" ? "Cartera Disponible" : "Carga Total"}
                </div>
                <div className="fs-4 fw-bold text-dark">
                  {activeTab === "sales" ? `${metrics.carteraCount} lugares` : `${metrics.totalPackages} bultos`}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* KPI 5: Backlog / Atrasadas */}
        <Col xs={12} sm={6} md={4} lg={2.4} className="col-lg">
          <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
            <Card.Body className="p-3 d-flex align-items-center gap-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-danger bg-danger-subtle"
                style={{ width: "46px", height: "46px" }}
              >
                <i className="fas fa-exclamation-triangle fs-5" />
              </div>
              <div>
                <div className="text-muted small fw-semibold text-uppercase" style={{ fontSize: "11px" }}>
                  {activeTab === "sales" ? "Revisitas Pendientes" : "Despachos Atrasados"}
                </div>
                <div className="fs-4 fw-bold text-danger d-flex align-items-baseline gap-1">
                  {metrics.backlogCount}
                  <span className="small text-muted fw-normal" style={{ fontSize: "12px" }}>
                    en espera
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 3. Main Workspace: 6 Day Weekly Calendar Grid (Full Width) */}
      <div
        className="d-flex gap-3 overflow-x-auto pb-3 w-100"
        style={{
          scrollbarWidth: "thin",
          minHeight: "720px",
        }}
      >
        {weekDates.map((day) => {
          const dayStops = activeStops
            .filter((s) => s.dayOfWeek === day.dayIndex)
            .sort((a, b) => a.orderIndex - b.orderIndex)

          const completedInDay = dayStops.filter((s) => s.status === "completed").length
          const isOverloaded = dayStops.length > 8

          return (
            <div
              key={day.dayIndex}
              className="flex-fill d-flex flex-column bg-white rounded-4 shadow-sm border p-3"
              style={{
                minWidth: "220px",
                flex: "1 1 0px",
                backgroundColor: day.isToday ? "#f8fbff" : "#ffffff",
                borderColor: day.isToday ? "#9ec5fe" : "#e2e8f0",
              }}
            >
                {/* Column Day Header */}
                <div className="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom">
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-uppercase text-dark" style={{ fontSize: "14px" }}>
                        {day.name}
                      </span>
                      {day.isToday && (
                        <span
                          className="badge bg-primary text-white rounded-pill px-2 py-0.5 ms-2"
                          style={{ fontSize: "9px", fontWeight: 700, letterSpacing: "0.5px" }}
                        >
                          HOY
                        </span>
                      )}
                    </div>
                    <small className="text-muted fw-semibold">{day.displayDate}</small>
                  </div>

                  <div className="text-end">
                    <span
                      className={`badge ${
                        isOverloaded
                          ? "bg-danger"
                          : dayStops.length === 0
                          ? "bg-light text-muted border"
                          : "bg-primary-subtle text-primary border border-primary-subtle"
                      } rounded-pill px-2 py-1 fw-bold`}
                    >
                      {dayStops.length} {activeTab === "sales" ? "visitas" : "entregas"}
                    </span>
                  </div>
                </div>

                {/* Daily action bar */}
                <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                  <small className="text-muted fw-semibold" style={{ fontSize: "12px" }}>
                    {completedInDay}/{dayStops.length} completadas
                  </small>
                  <div className="d-flex gap-2">
                    <Button
                      variant="light"
                      size="sm"
                      className="p-1 px-2 text-primary border rounded-circle shadow-xs"
                      title="Ver mapa de este día"
                      onClick={() => {
                        setSelectedDayForMap(day.dayIndex)
                        setShowMapModal(true)
                      }}
                    >
                      <i className="fas fa-map-marked-alt" />
                    </Button>
                    <Button
                      variant="light"
                      size="sm"
                      className="p-1 px-2 text-success border rounded-circle shadow-xs"
                      title={activeTab === "sales" ? "Agendar visita este día" : "Agregar entrega este día"}
                      onClick={() => {
                        setSelectedDayForAdd(day.dayIndex)
                        setNewStopForm((prev) => ({ ...prev, dayOfWeek: day.dayIndex }))
                        setShowAddStopModal(true)
                      }}
                    >
                      <i className="fas fa-plus" />
                    </Button>
                  </div>
                </div>

                {/* Stop Cards in this Day */}
                <div className="flex-grow-1 overflow-y-auto pe-1" style={{ maxHeight: "620px" }}>
                  {dayStops.length === 0 ? (
                    <div
                      className="d-flex flex-column align-items-center justify-content-center py-5 px-3 text-center rounded-3 border border-2 border-dashed h-100"
                      style={{
                        backgroundColor: "#fbfcfd",
                        borderColor: "#e2e8f0",
                        minHeight: "220px",
                      }}
                    >
                      <div
                        className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center mb-2"
                        style={{ width: "44px", height: "44px" }}
                      >
                        <i className="far fa-calendar-plus fs-5" />
                      </div>
                      <div className="fw-bold text-secondary small mb-1">
                        {activeTab === "sales" ? "Sin visitas agendadas" : "Sin despachos programados"}
                      </div>
                      <div className="text-muted small mb-3" style={{ fontSize: "11px" }}>
                        Selecciona clientes de la cartera o agenda uno nuevo
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="rounded-pill px-3 py-1 fw-semibold"
                        style={{ fontSize: "12px" }}
                        onClick={() => {
                          setSelectedDayForAdd(day.dayIndex)
                          setNewStopForm((prev) => ({ ...prev, dayOfWeek: day.dayIndex }))
                          setShowAddStopModal(true)
                        }}
                      >
                        <i className="fas fa-plus me-1" />
                        {activeTab === "sales" ? "Agendar Visita" : "Asignar Entrega"}
                      </Button>
                    </div>
                  ) : (
                    dayStops.map((stop, idx) => {
                      const userTheme = getUserColorTheme(stop.assignedName || stop.assignedTo)

                      return (
                        <div
                          key={stop.id}
                          className="p-3 mb-2.5 rounded-3 border bg-white shadow-xs position-relative hover-shadow"
                          style={{
                            borderLeft: `4px solid ${
                              stop.status === "completed"
                                ? "#198754"
                                : stop.status === "in_transit"
                                ? "#0d6efd"
                                : "#f59e0b"
                            }`,
                            transition: "all 0.15s ease-in-out",
                          }}
                        >
                          {/* Card Top: Order Pill & Person Badge with system color */}
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="badge bg-dark rounded-pill fw-bold" style={{ fontSize: "11px" }}>
                              #{stop.orderIndex || idx + 1}
                            </span>

                            {/* Person Assigned Pill */}
                            <div
                              className="d-flex align-items-center gap-1 px-2 py-0.5 rounded-pill"
                              style={{
                                backgroundColor: userTheme.bg,
                                color: userTheme.text,
                                border: `1px solid ${userTheme.border}`,
                                fontSize: "11px",
                                fontWeight: 600,
                              }}
                              title={`Asignado a: ${stop.assignedName || "Sin asignar"}`}
                            >
                              <span
                                className="rounded-circle d-inline-flex align-items-center justify-content-center text-white"
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  fontSize: "9px",
                                  backgroundColor: userTheme.darkBadge,
                                }}
                              >
                                {getInitials(stop.assignedName)}
                              </span>
                              <span className="text-truncate" style={{ maxWidth: "110px" }}>
                                {formatShortName(stop.assignedName) || "Sin asignar"}
                              </span>
                            </div>

                            {/* Status button toggle with high contrast */}
                            <button
                              className="btn btn-xs p-0 border-0"
                              style={{
                                color:
                                  stop.status === "completed"
                                    ? "#16a34a"
                                    : stop.status === "in_transit"
                                    ? "#0284c7"
                                    : "#b45309",
                              }}
                              onClick={() => handleToggleStatus(stop.id)}
                              title={`Estado: ${stop.status}. Clic para alternar.`}
                            >
                              <i
                                className={`fas ${
                                  stop.status === "completed"
                                    ? "fa-check-circle"
                                    : stop.status === "in_transit"
                                    ? "fa-truck"
                                    : "fa-clock"
                                } fs-6`}
                              />
                            </button>
                          </div>

                          {/* Client / Place Name & Direct Link */}
                          <div className="d-flex align-items-start justify-content-between gap-1 mb-1">
                            <div
                              className="fw-bold text-dark text-truncate cursor-pointer hover-text-primary"
                              style={{ fontSize: "13px" }}
                              title={stop.placeName}
                              onClick={() => setSelectedStopDetail(stop)}
                            >
                              {stop.placeName}
                            </div>
                            {stop.placeId && (
                              <button
                                className="btn btn-link btn-xs p-0 text-primary border-0 ms-1 flex-shrink-0"
                                title="Ir a la Ficha del Lugar"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  history.push(`${Routes.PlaceDetails.clean}${stop.placeId}`)
                                }}
                              >
                                <i className="fas fa-external-link-alt" style={{ fontSize: "11px" }} />
                              </button>
                            )}
                          </div>

                          {/* Sector / Address */}
                          <div className="small text-truncate mb-2" title={stop.address} style={{ fontSize: "11px", color: "#475569" }}>
                            <i className="fas fa-map-marker-alt me-1 text-danger" style={{ fontSize: "10px" }} />
                            {stop.sector || stop.address}
                          </div>

                          {/* Tab specific meta: Stage & Status Badge */}
                          {activeTab === "sales" ? (
                            <div className="d-flex justify-content-between align-items-center pt-2 border-top gap-1">
                              {stop.stageName ? (
                                <Badge
                                  bg="light"
                                  className="text-secondary border text-truncate d-inline-flex align-items-center"
                                  style={{ fontSize: "10px", maxWidth: "120px" }}
                                  title={`Etapa: ${stop.stageName}`}
                                >
                                  <i className="fas fa-layer-group me-1 text-muted" aria-hidden="true" />
                                  <span className="text-truncate">{stop.stageName}</span>
                                </Badge>
                              ) : <span />}
                              <span
                                className={`badge rounded-pill fw-semibold text-nowrap d-inline-flex align-items-center ${
                                  stop.status === "completed"
                                    ? "bg-success-subtle text-success border border-success-subtle"
                                    : "bg-warning-subtle text-warning-emphasis border border-warning-subtle"
                                }`}
                                style={{ fontSize: "10px" }}
                              >
                                <i
                                  className={`fas ${
                                    stop.status === "completed" ? "fa-check-circle text-success" : "fa-clock text-warning"
                                  } me-1`}
                                  aria-hidden="true"
                                />
                                <span>{stop.status === "completed" ? (stop.completedAt || "Visitado") : "Pendiente"}</span>
                              </span>
                            </div>
                          ) : (
                            <div className="d-flex justify-content-between align-items-center pt-2 border-top gap-1">
                              <span className="badge bg-light text-dark font-monospace border text-truncate" style={{ fontSize: "10px", maxWidth: "120px" }}>
                                {stop.conduceNumber || stop.invoiceNumber || "Sin doc"}
                              </span>
                              {stop.packageCount ? (
                                <span className="small text-muted text-nowrap d-inline-flex align-items-center" style={{ fontSize: "11px" }}>
                                  <i className="fas fa-box me-1 text-secondary" aria-hidden="true" />
                                  {stop.packageCount} bultos
                                </span>
                              ) : null}
                            </div>
                          )}

                          {/* Stop Card Actions Footer */}
                          <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                            <div className="btn-group btn-group-sm">
                              <Button
                                variant="light"
                                size="sm"
                                className="p-0 px-1.5 text-muted border d-inline-flex align-items-center justify-content-center"
                                onClick={() => handleReorder(stop.id, "up")}
                                disabled={idx === 0}
                                title="Subir orden"
                              >
                                <i className="fas fa-chevron-up" style={{ fontSize: "9px" }} />
                              </Button>
                              <Button
                                variant="light"
                                size="sm"
                                className="p-0 px-1.5 text-muted border d-inline-flex align-items-center justify-content-center"
                                onClick={() => handleReorder(stop.id, "down")}
                                disabled={idx === dayStops.length - 1}
                                title="Bajar orden"
                              >
                                <i className="fas fa-chevron-down" style={{ fontSize: "9px" }} />
                              </Button>
                            </div>

                            <div className="d-flex align-items-center gap-1.5">
                              {/* Move to another day dropdown */}
                              <Dropdown align="end">
                                <Dropdown.Toggle
                                  variant="light"
                                  size="sm"
                                  className="p-1 px-1.5 border text-muted"
                                  title="Mover a otro día"
                                >
                                  <i className="far fa-calendar-alt small" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="shadow-sm">
                                  <Dropdown.Header className="small text-uppercase">
                                    Mover a otro día
                                  </Dropdown.Header>
                                  {DAYS.filter((d) => d.dayIndex !== day.dayIndex).map((d) => (
                                    <Dropdown.Item
                                      key={d.dayIndex}
                                      onClick={() => handleChangeDay(stop.id, d.dayIndex)}
                                      className="small d-flex align-items-center"
                                    >
                                      <i className="far fa-calendar-alt me-2 text-primary" />
                                      {d.name}
                                    </Dropdown.Item>
                                  ))}
                                </Dropdown.Menu>
                              </Dropdown>

                              {/* Stop Detail View */}
                              <Button
                                variant="light"
                                size="sm"
                                className="p-1 px-1.5 border text-muted"
                                onClick={() => setSelectedStopDetail(stop)}
                                title="Ver detalle completo"
                              >
                                <i className="far fa-eye small" />
                              </Button>

                              {/* Remove Stop */}
                              <Button
                                variant="light"
                                size="sm"
                                className="p-1 px-1.5 border text-danger"
                                onClick={() => handleRemoveStop(stop)}
                                title="Quitar de este día (desasignar)"
                              >
                                <i className="far fa-trash-alt small" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            )
          })}
      </div>

      {/* MODAL 1: Importar Despacho (PDF o Imagen) */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5 fw-bold d-flex align-items-center gap-2">
            <i className="fas fa-file-invoice text-primary" />
            Importar Despacho (PDF o Foto de Conduce)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3 text-muted small">
            Sube un archivo <strong>PDF digital</strong> o una <strong>foto/escaneo (JPG, PNG)</strong> del conduce físico.
            El sistema extraerá los datos automáticamente y te permitirá asignarlo a un día de la semana o guardarlo en pendientes.
          </div>

          <div
            className="border border-2 border-dashed rounded-3 p-4 text-center bg-light mb-3 cursor-pointer hover-bg-light"
            onClick={() => document.getElementById("dispatch-file-input")?.click()}
          >
            <input
              type="file"
              id="dispatch-file-input"
              className="d-none"
              accept=".pdf,image/png,image/jpeg,image/jpg"
              onChange={handleFileSelect}
            />
            {isExtracting ? (
              <div className="py-3">
                <Spinner animation="border" variant="primary" className="mb-2" />
                <div className="fw-semibold text-dark">Analizando documento con OCR...</div>
                <small className="text-muted">Extrayendo conduce, cliente y bultos</small>
              </div>
            ) : importFile ? (
              <div className="py-2">
                <i className="fas fa-check-circle fa-2x text-success mb-2" />
                <div className="fw-bold text-dark">{importFile.name}</div>
                <small className="text-muted">
                  {(importFile.size / 1024).toFixed(1)} KB • Clic para cambiar archivo
                </small>
              </div>
            ) : (
              <div className="py-3">
                <i className="fas fa-cloud-upload-alt fa-3x text-primary mb-2" />
                <div className="fw-semibold text-dark">
                  Haz clic para seleccionar o arrastra un PDF o Imagen aquí
                </div>
                <small className="text-muted">Formatos admitidos: .pdf, .jpg, .png</small>
              </div>
            )}
          </div>

          {extractedData && (
            <Card className="border-primary-subtle bg-primary-subtle p-3">
              <div className="fw-bold text-primary mb-2 d-flex align-items-center gap-2">
                <i className="fas fa-robot" /> Datos Detectados del Documento
              </div>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">No. Conduce</Form.Label>
                    <Form.Control
                      size="sm"
                      value={extractedData.conduceNumber}
                      onChange={(e) =>
                        setExtractedData({ ...extractedData, conduceNumber: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">No. Factura</Form.Label>
                    <Form.Control
                      size="sm"
                      value={extractedData.invoiceNumber}
                      onChange={(e) =>
                        setExtractedData({ ...extractedData, invoiceNumber: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">Cliente / Lugar</Form.Label>
                    <Form.Control
                      size="sm"
                      value={extractedData.clientName}
                      onChange={(e) =>
                        setExtractedData({ ...extractedData, clientName: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">Dirección / Sector</Form.Label>
                    <Form.Control
                      size="sm"
                      value={extractedData.address}
                      onChange={(e) =>
                        setExtractedData({ ...extractedData, address: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">Cantidad de Bultos</Form.Label>
                    <Form.Control
                      type="number"
                      size="sm"
                      value={extractedData.packageCount}
                      onChange={(e) =>
                        setExtractedData({
                          ...extractedData,
                          packageCount: Number(e.target.value),
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold mb-1">Asignar al Día</Form.Label>
                    <Form.Select
                      size="sm"
                      value={extractedData.targetDay}
                      onChange={(e) =>
                        setExtractedData({
                          ...extractedData,
                          targetDay: Number(e.target.value),
                        })
                      }
                    >
                      {DAYS.map((d) => (
                        <option key={d.dayIndex} value={d.dayIndex}>
                          {d.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => setShowImportModal(false)}>
            Cancelar
          </Button>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              disabled={!extractedData}
              onClick={() => handleConfirmImport("backlog")}
            >
              Guardar en Pendientes
            </Button>
            <Button
              variant="primary"
              disabled={!extractedData}
              onClick={() => handleConfirmImport("week")}
            >
              Asignar a la Semana
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* MODAL 2: Vista de Recorrido en Mapa */}
      <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5 fw-bold d-flex align-items-center gap-2">
            <i className="fas fa-map-marked-alt text-primary" />
            Recorrido del Día: {DAYS.find((d) => d.dayIndex === selectedDayForMap)?.name} (
            {activeTab === "sales" ? "Visitas Comerciales" : "Despachos"})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="p-3 bg-light border-bottom d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2">
              {DAYS.map((d) => (
                <Button
                  key={d.dayIndex}
                  variant={selectedDayForMap === d.dayIndex ? "primary" : "outline-secondary"}
                  size="sm"
                  onClick={() => setSelectedDayForMap(d.dayIndex)}
                >
                  {d.shortName}
                </Button>
              ))}
            </div>
            <div className="small fw-semibold text-muted">
              {activeStops.filter((s) => s.dayOfWeek === selectedDayForMap).length} Paradas en secuencia
            </div>
          </div>

          <div className="p-4 text-center bg-white" style={{ minHeight: "350px", position: "relative" }}>
            <div className="text-start">
              <h6 className="fw-bold text-dark mb-3">Secuencia de Paradas Planificada:</h6>
              <div className="position-relative ps-4 border-start border-3 border-primary ms-3">
                {activeStops
                  .filter((s) => s.dayOfWeek === selectedDayForMap)
                  .sort((a, b) => a.orderIndex - b.orderIndex)
                  .map((stop, idx) => {
                    const userTheme = getUserColorTheme(stop.assignedName)
                    return (
                      <div key={stop.id} className="position-relative mb-3 pb-2">
                        <span
                          className="position-absolute bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-bold shadow-xs"
                          style={{
                            width: "26px",
                            height: "26px",
                            left: "-31px",
                            top: "0px",
                            fontSize: "12px",
                          }}
                        >
                          {idx + 1}
                        </span>
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-bold text-dark">{stop.placeName}</span>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: userTheme.bg,
                              color: userTheme.text,
                              border: `1px solid ${userTheme.border}`,
                              fontSize: "10px",
                            }}
                          >
                            <i className="fas fa-user me-1" />
                            {formatShortName(stop.assignedName) || "Sin asignar"}
                          </span>
                        </div>
                        <div className="text-muted small">
                          <i className="fas fa-map-pin me-1 text-danger" />
                          {stop.address}
                        </div>
                        <div className="mt-1 d-flex gap-2 align-items-center">
                          <Badge bg="light" className="text-dark font-monospace border">
                            {stop.conduceNumber || (activeTab === "sales" ? "Visita Comercial" : "Sin doc")}
                          </Badge>
                          <span className="small text-muted">
                            Estado:{" "}
                            <strong className={stop.status === "completed" ? "text-success" : "text-primary"}>
                              {stop.status === "completed" ? "Realizado" : "Pendiente"}
                            </strong>
                          </span>
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMapModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL 3: Agregar Parada / Agendar Visita */}
      <Modal show={showAddStopModal} onHide={() => setShowAddStopModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="h5 fw-bold">
            <i className="fas fa-plus-circle text-success me-2" />
            {activeTab === "sales" ? "Agendar Visita Comercial a Cliente / Obra" : "Agregar Despacho a la Ruta"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {placesList.length > 0 && (
              <Form.Group className="mb-3 p-2 bg-light rounded-3 border">
                <Form.Label className="small fw-bold text-uppercase text-secondary mb-1">
                  🔍 Seleccionar de Cartera de Clientes / Obras Existentes
                </Form.Label>
                <Form.Select
                  size="sm"
                  onChange={(e) => handleSelectExistingPlace(e.target.value)}
                  defaultValue=""
                >
                  <option value="">-- Buscar o seleccionar un cliente de la lista --</option>
                  {placesList.map((p) => (
                    <option key={p.id} value={p.id}>
                      🏢 {p.name} {p.sector ? `(${p.sector})` : ""}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <Row className="g-2 mb-2">
              <Col md={7}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Nombre del Cliente / Obra</Form.Label>
                  <Form.Control
                    placeholder="Ej. Ferretería El Sol o Torre Bella Vista"
                    value={newStopForm.placeName}
                    onChange={(e) => setNewStopForm({ ...newStopForm, placeName: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Sector / Ciudad</Form.Label>
                  <Form.Control
                    placeholder="Ej. Gurabo, Santiago"
                    value={newStopForm.sector}
                    onChange={(e) => setNewStopForm({ ...newStopForm, sector: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Label className="small fw-semibold">Dirección</Form.Label>
              <Form.Control
                placeholder="Ej. Av. Juan Pablo Duarte #15"
                value={newStopForm.address}
                onChange={(e) => setNewStopForm({ ...newStopForm, address: e.target.value })}
              />
            </Form.Group>

            <Row className="g-2 mb-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">
                    {activeTab === "sales" ? "Vendedor Responsable" : "Chofer / Camión Asignado"}
                  </Form.Label>
                  <Form.Select
                    value={newStopForm.assignedTo}
                    onChange={(e) => setNewStopForm({ ...newStopForm, assignedTo: e.target.value })}
                  >
                    <option value="">-- Seleccionar persona asignada --</option>
                    {agents.map((agt) => (
                      <option key={agt.id} value={agt.id}>
                        {agt.name} ({agt.role})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">Día de la Semana</Form.Label>
                  <Form.Select
                    value={newStopForm.dayOfWeek}
                    onChange={(e) =>
                      setNewStopForm({ ...newStopForm, dayOfWeek: Number(e.target.value) })
                    }
                  >
                    {DAYS.map((d) => (
                      <option key={d.dayIndex} value={d.dayIndex}>
                        {d.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {activeTab === "sales" ? (
              <Row className="g-2 mb-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Contacto en el Lugar</Form.Label>
                    <Form.Control
                      placeholder="Ej. Ing. Rafael Santos (809-555-0124)"
                      value={newStopForm.contactPerson}
                      onChange={(e) =>
                        setNewStopForm({ ...newStopForm, contactPerson: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Etapa de la Obra</Form.Label>
                    <Form.Select
                      value={newStopForm.stageName}
                      onChange={(e) =>
                        setNewStopForm({ ...newStopForm, stageName: e.target.value })
                      }
                    >
                      <option value="Movimiento de Tierra">Movimiento de Tierra</option>
                      <option value="Estructura">Estructura</option>
                      <option value="Terminación">Terminación</option>
                      <option value="Cliente Frecuente">Cliente Frecuente</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            ) : (
              <Row className="g-2 mb-2">
                <Col xs={4}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">No. Conduce</Form.Label>
                    <Form.Control
                      placeholder="CD-12345"
                      value={newStopForm.conduceNumber}
                      onChange={(e) =>
                        setNewStopForm({ ...newStopForm, conduceNumber: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col xs={4}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">No. Factura</Form.Label>
                    <Form.Control
                      placeholder="F-67890"
                      value={newStopForm.invoiceNumber}
                      onChange={(e) =>
                        setNewStopForm({ ...newStopForm, invoiceNumber: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col xs={4}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Bultos</Form.Label>
                    <Form.Control
                      type="number"
                      value={newStopForm.packageCount}
                      onChange={(e) =>
                        setNewStopForm({
                          ...newStopForm,
                          packageCount: Number(e.target.value) || 1,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Form.Group className="mb-2">
              <Form.Label className="small fw-semibold">Notas / Objetivo</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder={
                  activeTab === "sales"
                    ? "Objetivo de la visita comercial o seguimiento"
                    : "Instrucciones de descarga y entrega"
                }
                value={newStopForm.notes}
                onChange={(e) => setNewStopForm({ ...newStopForm, notes: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowAddStopModal(false)}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleCreateStop}>
            {activeTab === "sales" ? "Agendar Visita" : "Guardar Parada"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL 4: Detalle de Parada / Visita */}
      {selectedStopDetail && (
        <Modal show={true} onHide={() => setSelectedStopDetail(null)} centered>
          <Modal.Header closeButton className="bg-light">
            <Modal.Title className="h5 fw-bold d-flex align-items-center gap-2">
              <i className="fas fa-building text-primary" />
              {selectedStopDetail.category === "sales" ? "Detalle de Visita Comercial" : "Detalle de Despacho"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3">
            <div className="d-flex justify-content-between align-items-start mb-3 pb-2 border-bottom">
              <div>
                <h5 className="fw-bold text-dark mb-1">{selectedStopDetail.placeName}</h5>
                <p className="text-muted small mb-0">
                  <i className="fas fa-map-marker-alt text-danger me-1" />
                  {selectedStopDetail.address} {selectedStopDetail.sector ? `(${selectedStopDetail.sector})` : ""}
                </p>
              </div>
              {selectedStopDetail.placeId && (
                <Button
                  variant="primary"
                  size="sm"
                  className="d-flex align-items-center gap-1.5 shadow-xs text-nowrap ms-2"
                  onClick={() => {
                    history.push(`${Routes.PlaceDetails.clean}${selectedStopDetail.placeId}`)
                  }}
                  title="Abrir ficha completa del lugar / cliente"
                >
                  <i className="fas fa-external-link-alt" />
                  Ver Ficha del Lugar
                </Button>
              )}
            </div>

            <Table bordered hover size="sm" className="small">
              <tbody>
                <tr>
                  <td className="fw-semibold text-muted bg-light" style={{ width: "35%" }}>
                    Responsable Asignado
                  </td>
                  <td>
                    {(() => {
                      const userTheme = getUserColorTheme(selectedStopDetail.assignedName)
                      return (
                        <div
                          className="d-inline-flex align-items-center gap-1 px-2 py-0.5 rounded-pill"
                          style={{
                            backgroundColor: userTheme.bg,
                            color: userTheme.text,
                            border: `1px solid ${userTheme.border}`,
                            fontWeight: 600,
                          }}
                        >
                          <span
                            className="rounded-circle d-inline-flex align-items-center justify-content-center text-white"
                            style={{
                              width: "16px",
                              height: "16px",
                              fontSize: "9px",
                              backgroundColor: userTheme.darkBadge,
                            }}
                          >
                            {getInitials(selectedStopDetail.assignedName)}
                          </span>
                          <span>{selectedStopDetail.assignedName || "Sin asignar"}</span>
                        </div>
                      )
                    })()}
                  </td>
                </tr>
                <tr>
                  <td className="fw-semibold text-muted bg-light">Estado</td>
                  <td>
                    <Badge
                      bg={
                        selectedStopDetail.status === "completed"
                          ? "success"
                          : selectedStopDetail.status === "in_transit"
                          ? "primary"
                          : "warning"
                      }
                    >
                      {selectedStopDetail.status === "completed"
                        ? "Realizado / Entregado"
                        : selectedStopDetail.status === "in_transit"
                        ? "En Tránsito"
                        : "Pendiente"}
                    </Badge>
                    {selectedStopDetail.completedAt && (
                      <span className="ms-2 text-muted">({selectedStopDetail.completedAt})</span>
                    )}
                  </td>
                </tr>
                {selectedStopDetail.category === "sales" ? (
                  <>
                    <tr>
                      <td className="fw-semibold text-muted bg-light">Contacto</td>
                      <td>{selectedStopDetail.contactPerson || "Encargado de obra"}</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold text-muted bg-light">Etapa Obra</td>
                      <td>{selectedStopDetail.stageName || "General"}</td>
                    </tr>
                  </>
                ) : (
                  <>
                    <tr>
                      <td className="fw-semibold text-muted bg-light">Conduce</td>
                      <td className="font-monospace">{selectedStopDetail.conduceNumber || "—"}</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold text-muted bg-light">Factura</td>
                      <td className="font-monospace">{selectedStopDetail.invoiceNumber || "—"}</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold text-muted bg-light">Bultos</td>
                      <td>
                        <i className="fas fa-box me-1 text-secondary" />
                        {selectedStopDetail.packageCount || 1}
                      </td>
                    </tr>
                  </>
                )}
                {selectedStopDetail.notes && (
                  <tr>
                    <td className="fw-semibold text-muted bg-light">Notas</td>
                    <td>{selectedStopDetail.notes}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between bg-light">
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => {
                handleRemoveStop(selectedStopDetail)
                setSelectedStopDetail(null)
              }}
            >
              <i className="far fa-trash-alt me-1" />
              Devolver a Cartera / Quitar
            </Button>
            <div className="d-flex gap-2">
              {selectedStopDetail.placeId && (
                <Button
                  variant="primary"
                  size="sm"
                  className="fw-semibold"
                  onClick={() => {
                    history.push(`${Routes.PlaceDetails.clean}${selectedStopDetail.placeId}`)
                  }}
                >
                  <i className="fas fa-building me-1" />
                  Ver Ficha Completa
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={() => setSelectedStopDetail(null)}>
                Cerrar
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  )
}
